/**
 * GOAL: Fetch configuration from script attributes.
 */

import { ScriptConfig } from "./types";
import { logWarning, logError } from "./utils";

/**
 * Parse script configuration from HTML attributes
 */
export function parseConfig(): ScriptConfig | null {
  const currentScript = document.currentScript as HTMLScriptElement;
  if (!currentScript) {
    logError("Unable to find current script element");
    return null;
  }

  const attributePrefix = "trak-";
  const getAttribute = (name: string): string | null =>
    currentScript.getAttribute(attributePrefix + name);

  // Get required attributes
  const websiteId = getAttribute("web-id");
  const domain = getAttribute("domain");

  // Check for required attributes: websiteId (trak-web-id) and domain (trak-domain)
  // If either is missing, log a warning and return null
  if (!websiteId || !domain) {
    logWarning("Missing required trak-web-id or trak-domain attribute");
    return null;
  }

  // Determine API endpoint - Use build-time environment variable
  const apiEndpoint = process.env.ANALYTICS_HOST 
    ? `${process.env.ANALYTICS_HOST}/api/v1/push`
    : new URL('/api/v1/push', window.location.origin).href;

  // Parse optional attributes with defaults
  const debugMode = getAttribute("debug") === "true";
  const debounceDuration = parseInt(getAttribute("debounce") || "300", 10);
  const autoTrackPageview = getAttribute("auto-track") !== "false";

  const trackOutbound = getAttribute("track-outbound") !== "false";

  // Parse skip patterns
  const skipPatternsStr = getAttribute("skip-patterns") || "";
  const skipPatterns = skipPatternsStr
    ? skipPatternsStr
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
    : [];

  // Parse mask patterns
  const maskPatternsStr = getAttribute("mask-patterns") || "";
  const maskPatterns = maskPatternsStr
    ? maskPatternsStr
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
    : [];

  return {
    websiteId,
    domain,
    apiEndpoint,
    debugMode,
    debounceDuration,
    autoTrackPageview,

    trackOutbound,
    skipPatterns,
    maskPatterns,
  };
}

/**
 * Validate configuration
 */
export function validateConfig(config: ScriptConfig): boolean {
  if (!config.websiteId || typeof config.websiteId !== "string") {
    logError("Invalid websiteId in configuration");
    return false;
  }

  if (!config.domain || typeof config.domain !== "string") {
    logError("Invalid domain in configuration");
    return false;
  }

  if (!config.apiEndpoint || typeof config.apiEndpoint !== "string") {
    logError("Invalid apiEndpoint in configuration");
    return false;
  }

  try {
    new URL(config.apiEndpoint);
  } catch {
    logError("Invalid apiEndpoint URL format");
    return false;
  }

  if (
    typeof config.debounceDuration !== "number" ||
    config.debounceDuration < 0
  ) {
    logWarning("Invalid debounceDuration, using default 300ms");
    config.debounceDuration = 300;
  }

  return true;
}
